import { useState, useRef, useEffect, useCallback, useLayoutEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ThumbsUp,
  MessageCircle,
  MoreHorizontal,
  Pin,
  Edit3,
  Trash2,
  Rss,
  ExternalLink,
  Share2,
  Flag,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { FileTypeIcon } from '@/features/messaging/components/FileTypeIcon';
import { GlassCard } from '@/components/glass';
import { UserAvatar } from '@/components/ui';
import { useToggleReaction } from '../hooks';
import { ACTIVE_COLORS, EMOJI_MAP } from './ReactionButton';
import { ReactionSummary } from './ReactionSummary';
import { ReactionPicker } from './ReactionPicker';
import { getGradientById, gradientCSS } from '../constants';
import { platform } from '@/utils/platform';
import { ReportContentModal } from './ReportContentModal';
import type { FeedPost, ReactionType } from '../types';

function formatTimeAgo(isoString: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 60) return t('feed.time_just_now');
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return t('feed.time_minutes_ago', { count: diffMinutes });
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t('feed.time_hours_ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return t('feed.time_days_ago', { count: diffDays });
  const diffMonths = Math.floor(diffDays / 30);
  return t('feed.time_months_ago', { count: diffMonths });
}

/** Extract YouTube video ID from watch/embed/youtu.be URLs */
function extractYouTubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
  );
  return m?.[1] ?? null;
}

/** Returns true if the first alphabetic character is Hebrew. */
function isHebrew(text: string): boolean {
  const match = text.match(/[\p{L}]/u);
  return !!match && /[\u0590-\u05FF]/.test(match[0]);
}

interface PostCardProps {
  post: FeedPost;
  onComment: (post: FeedPost) => void;
  onEdit: (post: FeedPost) => void;
  onDelete: (id: string) => void;
  onPin: (id: string) => void;
  onReadArticle?: (post: FeedPost) => void;
  onAuthorClick?: (userId: string) => void;
}

/** RSS image that shows small images (logos, icons) at natural size instead of stretching. */
function RssImage({ src, alt, onClick }: { src: string; alt: string; onClick?: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<'loading' | 'small' | 'full'>('loading');

  const handleLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    // If natural width is less than half the card width → render at original size
    setLayout(img.naturalWidth > 0 && img.naturalWidth < containerWidth / 2 ? 'small' : 'full');
  }, []);

  return (
    <div
      ref={containerRef}
      className={`mt-1 ${layout === 'small' ? 'flex justify-center px-4 py-3' : ''} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    >
      <img
        src={src}
        alt={alt}
        className={
          layout === 'loading'
            ? 'w-full max-h-[300px] opacity-0'
            : layout === 'small'
              ? 'object-contain rounded-lg shadow-sm border border-black/[0.06]'
              : 'w-full object-cover max-h-[300px]'
        }
        onLoad={handleLoad}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    </div>
  );
}

/** Detects native aspect ratio and renders the video accordingly. */
function FeedVideo({ src }: { src: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPortrait, setIsPortrait] = useState(false);

  const handleMetadata = useCallback(() => {
    const v = videoRef.current;
    if (v && v.videoWidth && v.videoHeight) {
      setIsPortrait(v.videoHeight > v.videoWidth);
    }
  }, []);

  return (
    <video
      ref={videoRef}
      src={src}
      controls
      preload="metadata"
      onLoadedMetadata={handleMetadata}
      className={
        isPortrait
          ? 'w-full max-h-[480px] object-contain bg-black'
          : 'w-full max-h-[300px] object-cover'
      }
    />
  );
}

export const PostCard = memo(function PostCard({ post, onComment, onEdit, onDelete, onPin, onReadArticle, onAuthorClick }: PostCardProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { isAdmin } = usePermissions();
  const toggleReaction = useToggleReaction();

  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null);

  const isRssPost = post.source === 'rss';
  const isAuthor = user?.id === post.author.id;
  const canManage = isAuthor || isAdmin();

  // ─── Layout modes ────────────────────────────────────────────
  const gradient = getGradientById(post.bgGradient);
  const isGradientMode = !!gradient;
  const isShortText = !isGradientMode
    && post.content.length < 85
    && !post.content.includes('http')
    && !post.mediaUrl;

  // Gradient content text color (header is always white bg → dark text)
  const gradientTextColor = isGradientMode
    ? (gradient!.darkText ? '#050505' : '#FFFFFF')
    : '#050505';

  // Close menu on outside click; reposition on scroll/resize
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      const inBtn = menuRef.current?.contains(target);
      const inPortal = menuPortalRef.current?.contains(target);
      if (!inBtn && !inPortal) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('resize', updateMenuPos);
    window.addEventListener('scroll', updateMenuPos, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('resize', updateMenuPos);
      window.removeEventListener('scroll', updateMenuPos, true);
    };
  }, [menuOpen]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLikeMouseEnter = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    hoverTimerRef.current = setTimeout(() => setPickerOpen(true), 500);
  }, []);

  const handleLikeMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    closeTimerRef.current = setTimeout(() => setPickerOpen(false), 300);
  }, []);

  const handlePickerMouseEnter = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  }, []);

  const handlePickerMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setPickerOpen(false), 300);
  }, []);

  const handleReaction = useCallback(
    (type: ReactionType) => {
      toggleReaction.mutate({ postId: post.id, type });
    },
    [toggleReaction, post.id],
  );

  // Determine the user's active reaction for the Like button display
  const userLikeReaction = post.userReactions.length > 0 ? post.userReactions[0] : null;
  const likeColor = userLikeReaction ? ACTIVE_COLORS[userLikeReaction] : undefined;

  // ─── Shared header elements ──────────────────────────────────
  const handleAuthorClick = !isRssPost && onAuthorClick
    ? () => onAuthorClick(post.author.id)
    : undefined;

  const authorAvatar = isRssPost ? (
    <div
      className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{
        background: 'linear-gradient(135deg, #f97316, #ea580c)',
        border: '1px solid rgba(255,255,255,0.3)',
      }}
    >
      <Rss className="h-5 w-5 text-white" />
    </div>
  ) : (
    <UserAvatar
      src={post.author.avatarUrl}
      name={`${post.author.firstName ?? ''} ${post.author.lastName ?? ''}`}
      size="md"
      className="border border-white/30"
      onClick={handleAuthorClick}
    />
  );

  const tagged = post.taggedUsers ?? [];
  const handleTagClick = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onAuthorClick?.(userId);
  };
  const taggedLine = tagged.length > 0 && !isRssPost ? (() => {
    const getName = (u: { firstName: string | null; lastName: string | null }) =>
      [u.firstName, u.lastName].filter(Boolean).join(' ');
    const clickableTag = (u: { id: string; firstName: string | null; lastName: string | null }) => (
      <span
        className={`font-semibold${onAuthorClick ? ' cursor-pointer hover:underline' : ''}`}
        onClick={onAuthorClick ? (e) => handleTagClick(u.id, e) : undefined}
      >
        {getName(u)}
      </span>
    );
    if (tagged.length === 1) {
      return <>{t('feed.isWith')} {clickableTag(tagged[0])}</>;
    }
    if (tagged.length === 2) {
      return <>{t('feed.isWith')} {clickableTag(tagged[0])} {t('feed.and')} {clickableTag(tagged[1])}</>;
    }
    return <>{t('feed.isWith')} {clickableTag(tagged[0])}, {clickableTag(tagged[1])} {t('feed.and_others', { count: tagged.length - 2 })}</>;
  })() : null;

  const authorTitle = (
    <div className="flex items-center gap-3">
      {authorAvatar}
      <div className="min-w-0 flex flex-col justify-center h-10">
        <span className="font-semibold text-[14px] leading-4 truncate block" style={{ color: '#050505' }}>
          {isRssPost ? post.sourceName : (
            <span
              className={handleAuthorClick ? 'cursor-pointer hover:underline' : undefined}
              onClick={handleAuthorClick}
            >
              {`${post.author.firstName} ${post.author.lastName}`}
            </span>
          )}
          {taggedLine && (
            <span className="font-normal text-[12px]" style={{ color: '#65676B' }}>
              {' '}{taggedLine}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold leading-3.5" style={{ color: '#65676B' }}>
            {formatTimeAgo(post.createdAt, t)}
          </span>
          {isRssPost && (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
              style={{ backgroundColor: '#fff7ed', color: '#ea580c' }}
            >
              RSS
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const updateMenuPos = useCallback(() => {
    if (!menuBtnRef.current) return;
    const rect = menuBtnRef.current.getBoundingClientRect();
    const isRTL = document.documentElement.dir === 'rtl';
    setMenuPos({
      top: rect.bottom + 4,
      left: isRTL ? rect.left : rect.right,
    });
  }, []);

  // Calculate position when menu opens
  useLayoutEffect(() => {
    if (!menuOpen) { setMenuPos(null); return; }
    updateMenuPos();
  }, [menuOpen, updateMenuPos]);

  const menuDropdown = (
    <div ref={menuRef}>
      <button
        ref={menuBtnRef}
        onClick={() => setMenuOpen((v) => !v)}
        className={`p-1.5 rounded-lg transition-all active:scale-90 ${menuOpen ? 'bg-black/5' : ''}`}
        style={{ color: '#6B7280' }}
        aria-label={t('feed.post_menu')}
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>

      {menuOpen && menuPos && createPortal(
        <div
          ref={menuPortalRef}
          className="fixed rounded-xl overflow-hidden min-w-[160px] border"
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: '#E4E6EB',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            top: menuPos.top,
            left: menuPos.left,
            transform: document.documentElement.dir === 'rtl' ? undefined : 'translateX(-100%)',
          }}
        >
          {canManage && !isRssPost && (
            <button
              onClick={() => { setMenuOpen(false); onEdit(post); }}
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-neutral-800 transition-colors hover:bg-gray-100"
            >
              <Edit3 className="h-4 w-4" />
              {t('feed.edit_post')}
            </button>
          )}
          {canManage && (
            <button
              onClick={() => { setMenuOpen(false); onDelete(post.id); }}
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-bold transition-colors hover:bg-gray-100"
              style={{ color: '#ef4444' }}
            >
              <Trash2 className="h-4 w-4" />
              {t('feed.delete_post')}
            </button>
          )}
          {isAdmin() && (
            <button
              onClick={() => { setMenuOpen(false); onPin(post.id); }}
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-neutral-800 transition-colors hover:bg-gray-100 border-t"
              style={{ borderColor: '#E4E6EB' }}
            >
              <Pin className="h-4 w-4" />
              {post.isPinned ? t('feed.unpin_post') : t('feed.pin_post')}
            </button>
          )}
          {!isAuthor && (
            <button
              onClick={() => { setMenuOpen(false); setReportOpen(true); }}
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium transition-colors hover:bg-gray-100 border-t"
              style={{ color: '#ef4444', borderColor: '#E4E6EB' }}
            >
              <Flag className="h-4 w-4" />
              {t('feed.reportPost')}
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );

  const headerActionEl = (
    <div className="flex items-center gap-1">
      {post.isPinned && (
        <Pin className="h-3.5 w-3.5" style={{ color: '#9CA3AF' }} />
      )}
      {menuDropdown}
    </div>
  );

  // ─── Shared bottom sections ──────────────────────────────────
  const reactionSummary = (
    <ReactionSummary
      reactionCounts={post.reactionCounts}
      commentCount={post.commentCount}
      viewCount={post.viewCount}
      onCommentClick={() => onComment(post)}
    />
  );

  const actionBar = (
    <div className="flex items-center">
      <div
        className="relative flex-1"
        onMouseEnter={handleLikeMouseEnter}
        onMouseLeave={handleLikeMouseLeave}
      >
        <button
          onClick={() => handleReaction('like')}
          onContextMenu={(e) => { e.preventDefault(); setPickerOpen(true); }}
          disabled={toggleReaction.isPending}
          className="flex items-center justify-center gap-1.5 w-full py-2 rounded-md text-[15px] font-semibold transition-colors hover:bg-black/[0.03] active:scale-95 disabled:opacity-50"
          style={{ color: likeColor ?? '#65676B' }}
        >
          {userLikeReaction ? (
            <span className="text-lg leading-none">{EMOJI_MAP[userLikeReaction]}</span>
          ) : (
            <ThumbsUp className="h-5 w-5" />
          )}
          {userLikeReaction ? t(`feed.reactions.${userLikeReaction}`) : t('feed.like_action')}
        </button>
        <div
          onMouseEnter={handlePickerMouseEnter}
          onMouseLeave={handlePickerMouseLeave}
        >
          <ReactionPicker
            isOpen={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={handleReaction}
            activeReactions={post.userReactions}
          />
        </div>
      </div>
      <div className="w-px h-5" style={{ backgroundColor: '#CED0D4' }} />
      <button
        onClick={() => onComment(post)}
        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[15px] font-semibold transition-colors hover:bg-black/[0.03] active:scale-95"
        style={{ color: '#65676B' }}
      >
        <MessageCircle className="h-5 w-5" />
        {t('feed.comment_action')}
      </button>
      <div className="w-px h-5" style={{ backgroundColor: '#CED0D4' }} />
      <button
        onClick={() => {
          const url = post.sourceUrl || `${window.location.origin}/feed/${post.id}`;
          if (navigator.share) { void navigator.share({ url }); } else { void navigator.clipboard.writeText(url); }
        }}
        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[15px] font-semibold transition-colors hover:bg-black/[0.03] active:scale-95"
        style={{ color: '#65676B' }}
      >
        <Share2 className="h-5 w-5" />
        {t('feed.share_action')}
      </button>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // GRADIENT BACKGROUND MODE
  // ═══════════════════════════════════════════════════════════════
  if (isGradientMode) {
    return (
      <>
        <div className="feed-post-flat overflow-hidden bg-white" style={{ borderTop: '1px solid #CED0D4', borderBottom: '1px solid #CED0D4' }}>
          {/* White header — same as normal posts */}
          <div className="px-3 py-1.5 flex items-center justify-between">
            {authorTitle}
            {headerActionEl}
          </div>

          {/* Gradient content area */}
          <div
            className="flex items-center justify-center min-h-[280px] px-8 text-center"
            style={{ background: gradientCSS(gradient!) }}
          >
            <p
              className="font-bold leading-tight drop-shadow-sm whitespace-pre-wrap break-words max-w-full"
              style={{ fontSize: '32px', lineHeight: '38px', color: gradientTextColor }}
            >
              {post.content}
            </p>
          </div>

          {/* Reaction summary + action bar on white bg */}
          {reactionSummary}
          {actionBar}
        </div>
        <ReportContentModal
          isOpen={reportOpen}
          onClose={() => setReportOpen(false)}
          contentType="post"
          contentId={post.id}
        />
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // NORMAL / SHORT TEXT MODE (uses GlassCard)
  // ═══════════════════════════════════════════════════════════════
  return (
    <GlassCard
      padding="none"
      title={authorTitle}
      headerAction={headerActionEl}
      className="feed-post-flat"
      headerClassName="px-3 py-1.5 !border-b-0"
      headerStyle={{ backgroundColor: 'transparent' }}
      style={isRssPost ? {
        background: 'rgba(255,255,255,0.88)',
        borderColor: 'rgba(0,0,0,0.08)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      } : undefined}
    >

      {/* ─── RSS post: structured title + body + image + read link ─── */}
      {isRssPost ? (() => {
        // Split "title\n\nbody" stored in content
        const nlIdx = post.content.indexOf('\n\n');
        const rssTitle = nlIdx > -1 ? post.content.slice(0, nlIdx) : post.content;
        const rssBody = nlIdx > -1 ? post.content.slice(nlIdx + 2) : '';
        const rssDir = isHebrew(rssTitle || rssBody) ? 'rtl' : undefined;
        const ytId = extractYouTubeId(post.sourceUrl);

        return (
          <>
            {/* Title — bold, larger */}
            <div className="px-4 pt-3">
              <h3
                className="font-bold text-[16px] leading-5 break-words"
                style={{ color: '#050505' }}
                dir={rssDir}
              >
                {rssTitle}
              </h3>
            </div>

            {/* Body — regular */}
            {rssBody && (
              <div className="px-4 pt-1.5 pb-1">
                <p
                  className="text-[14px] leading-[1.45] whitespace-pre-wrap break-words"
                  style={{ color: '#333' }}
                  dir={rssDir}
                >
                  {rssBody}
                </p>
              </div>
            )}

            {/* "Watch on YouTube" link or "Read article" button — above media */}
            {post.sourceUrl && (
              <div className="px-4 py-1">
                {ytId ? (
                  <a
                    href={post.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors hover:underline"
                    style={{ color: '#ea580c' }}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t('feed.watchOnYoutube')}
                  </a>
                ) : (
                  <button
                    onClick={() => onReadArticle?.(post)}
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors hover:underline"
                    style={{ color: '#ea580c' }}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t('feed.readArticle')}
                  </button>
                )}
              </div>
            )}

            {/* YouTube embed — inline player (web) or thumbnail (native iOS/Android) */}
            {ytId ? (
              platform.isNative ? (
                /* Native: thumbnail + play button — avoids Error 153 (capacitor:// has no valid Referer) */
                <a
                  href={post.sourceUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 relative block aspect-video bg-black cursor-pointer group"
                >
                  <img
                    src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                    alt={rssTitle || 'YouTube video'}
                    className="w-full h-full object-cover"
                  />
                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-11 bg-[#cc0000] rounded-xl flex items-center justify-center group-active:bg-[#ff0000] transition-colors shadow-lg">
                      <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7 ms-0.5">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </a>
              ) : (
                <div className="mt-1 aspect-video">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${ytId}`}
                    title={rssTitle || 'YouTube video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              )
            ) : (
              /* RSS image — auto-sizes small images (logos) to natural size */
              post.mediaUrl && post.mediaType === 'image' && (
                <RssImage
                  src={post.mediaUrl}
                  alt={rssTitle || t('feed.post_media')}
                  onClick={() => onReadArticle?.(post)}
                />
              )
            )}
          </>
        );
      })() : (
        <>
          {/* ─── Regular post content — dynamic font sizing ─── */}
          {isShortText ? (
            <div className="px-4 flex flex-col justify-center items-center min-h-[120px] text-center">
              <p
                className="font-medium whitespace-pre-wrap break-words max-w-full"
                style={{ fontSize: '24px', lineHeight: '28px', color: '#050505' }}
              >
                {post.content}
              </p>
            </div>
          ) : (
            <div className="px-4 pt-2 pb-2">
              <p
                className="text-[15px] leading-5 whitespace-pre-wrap break-words"
                style={{ color: '#050505' }}
              >
                {post.content}
              </p>
            </div>
          )}
        </>
      )}

      {/* Media — edge-to-edge for images/video, padded for documents (non-RSS posts) */}
      {!isRssPost && post.mediaUrl && (
        post.mediaType === 'document' ? (
          <div className="px-4 pb-3">
            <FileTypeIcon
              fileName={post.mediaMeta?.thumbnail ?? post.mediaUrl.split('/').pop() ?? 'file'}
              url={post.mediaUrl}
            />
          </div>
        ) : post.mediaType === 'image' ? (
          <img
            src={post.mediaUrl}
            alt={t('feed.post_media')}
            className="w-full object-cover max-h-[400px]"
          />
        ) : post.mediaType === 'video' ? (
          <FeedVideo src={post.mediaUrl} />
        ) : null
      )}

      {/* Reaction summary + action bar */}
      {reactionSummary}
      {actionBar}

      <ReportContentModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        contentType="post"
        contentId={post.id}
      />
    </GlassCard>
  );
});
